// src/lib/device.ts
// terminalIdentificationNumber=deviceId, deviceType=OsCode(1:iOS,2:Android,3:Windows,9:その他)

import { storage } from "./storage";

export type OsCode = 1 | 2 | 3 | 9;

export type DeviceParams = {
  terminalIdentificationNumber: string;
  deviceType: OsCode;
};

const DEVICE_ID_KEY = "app_device_id";

/**
 * deviceId を取得（なければ生成して永続化）
 * 優先順位:
 *  1) storage（正）
 *  2) 互換: localStorage(旧キー) から取り込み
 *  3) 新規生成して storage + localStorage(互換) に保存
 */
export function getOrCreateDeviceId(): string {
  // SSR/テスト環境保険（ブラウザ以外では永続化不可なので都度生成）
  if (typeof window === "undefined") return createUuid();

  // 1) storage を正として読む
  const s = storage.getAuth();
  if (s?.terminalIdentificationNumber) return s.terminalIdentificationNumber;

  // 2) 旧キー（従来の保存先）があれば取り込み
  const existing = window.localStorage.getItem(DEVICE_ID_KEY);
  if (existing) {
    storage.setAuth({ terminalIdentificationNumber: existing });
    return existing;
  }

  // 3) 新規生成 → storage に保存（＋互換で旧キーにも保存）
  const id = createUuid();

  storage.setAuth({ terminalIdentificationNumber: id });
  window.localStorage.setItem(DEVICE_ID_KEY, id);

  return id;
}

/**
 * OSコード判定
 * 1:iOS, 2:Android, 3:Windows, 9:その他
 */
export function getOsCode(): OsCode {
  if (typeof navigator === "undefined") return 9;

  // userAgentData が取れる場合は優先（Chromium系）
  const uaData = (navigator as any).userAgentData;
  if (uaData?.platform) {
    const p = String(uaData.platform).toLowerCase();
    if (p.includes("android")) return 2;
    if (p.includes("windows")) return 3;
    // iOS は iPhone/iPad を含むこともあるが、念のため複数判定
    if (p.includes("ios") || p.includes("iphone") || p.includes("ipad")) return 1;
  }

  const ua = navigator.userAgent || "";
  const platform = String((navigator as any).platform || "").toLowerCase();

  // iPadOS は UA が Mac っぽくなることがあるため maxTouchPoints も見る
  const isIOS =
    /iPad|iPhone|iPod/i.test(ua) ||
    (platform === "macintel" && (navigator as any).maxTouchPoints > 1);

  if (isIOS) return 1;
  if (/Android/i.test(ua)) return 2;
  if (/Windows NT/i.test(ua) || platform.includes("win")) return 3;

  return 9;
}

/**
 * API に渡すデバイスパラメータ
 */
export function getDeviceParams(): DeviceParams {
  return {
    terminalIdentificationNumber: getOrCreateDeviceId(),
    deviceType: getOsCode(),
  };
}

/**
 * UUID生成:
 *  - crypto.randomUUID が使えればそれを優先
 *  - なければ Web Crypto(getRandomValues) で v4 生成
 *  - それも無理なら Math.random フォールバック（最終手段）
 */
function createUuid(): string {
  // randomUUID（存在していても function でない環境があるので typeof 判定）
  const c = globalThis.crypto as Crypto | undefined;

  if (typeof c?.randomUUID === "function") {
    return c.randomUUID();
  }

  return fallbackUuidV4();
}

function fallbackUuidV4(): string {
  const c = globalThis.crypto as Crypto | undefined;

  // Web Crypto が使えるなら v4 を生成
  if (typeof c?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);

    // RFC4122 v4
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant

    let hex = "";
    for (let i = 0; i < bytes.length; i++) {
      hex += bytes[i].toString(16).padStart(2, "0");
    }

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
      16,
      20
    )}-${hex.slice(20)}`;
  }

  // 最終手段（暗号学的に強くない）
  const s = () =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  return `${s()}${s()}-${s()}-${s()}-${s()}-${s()}${s()}${s()}`;
}
